import { useState, useCallback, useMemo, useEffect } from 'react';
import { UseOTCFiltersProps, UseOTCFiltersReturn } from '../types';
import { BASE_COLUMN_FIELDS, DATE_FORMATS } from '../OTCConstants';

export const useOTCFilters = ({
  searchFilteredData,
  updatedCells,
  columns,
  hiddenColumnFields,
  apiRef,
  setHiddenColumnFields,
  updateViewModifications,
  viewManager,
  isGroupingEnabled,
  showOnlyIllioDifferences,
  hideEmptyDateColumns,
  setHideEmptyDateColumns,
}: UseOTCFiltersProps): UseOTCFiltersReturn => {
  // Main filtered data - combines Illio differences filtering
  // Note: searchFilteredData is already filtered by view selection in the parent component
  const filteredData = useMemo(() => {
    let data = searchFilteredData;

    // Apply Illio differences filter if enabled
    if (showOnlyIllioDifferences) {
      data = data.filter(row => {
        const rowId = (row._id || row.id) as string;
        return updatedCells[rowId] && updatedCells[rowId].size > 0;
      });
    }

    return data;
  }, [searchFilteredData, showOnlyIllioDifferences, updatedCells]);

  // Apply hide empty date columns programmatically (for view restoration)
  const applyHideEmptyDateColumns = useCallback(
    (shouldHide: boolean) => {
      setHideEmptyDateColumns(shouldHide);

      if (shouldHide) {
        // If turning ON the filter, Hide Empty Dates

        // Find columns with values in current filtered data
        const columnsWithValues = new Set<string>();

        // Check all rows to find which date columns have values
        filteredData.forEach(row => {
          Object.entries(row).forEach(([key, value]) => {
            if (
              DATE_FORMATS.YYYY_MM_DD_REGEX.test(key) &&
              value !== null &&
              value !== undefined &&
              value !== ''
            ) {
              columnsWithValues.add(key);
            }
          });
        });

        // Get all date columns from current columns
        const dateColumns = columns
          .filter(col => DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field))
          .map(col => col.field);

        // Find empty date columns
        const emptyDateColumns = dateColumns.filter(
          field => !columnsWithValues.has(field)
        );

        // Add all empty date columns to hidden fields
        const newHiddenFields = new Set(hiddenColumnFields);
        emptyDateColumns.forEach(field => newHiddenFields.add(field));

        // Update state
        setHiddenColumnFields(newHiddenFields);

        // Update the visibility model directly
        const currentVisibility =
          apiRef.current?.state.columns.columnVisibilityModel || {};
        const newVisibility = { ...currentVisibility };

        // Hide Empty Dates
        emptyDateColumns.forEach(field => {
          newVisibility[field] = false;
        });

        // Ensure group_name is always visible when groups are enabled
        if (isGroupingEnabled) {
          newVisibility['group_name'] = true;
        }

        // Apply to grid
        if (apiRef.current) {
          apiRef.current.setColumnVisibilityModel(newVisibility);
        }
      } else {
        // If turning OFF the filter, show all date columns

        // Get all date columns
        const dateColumns = columns
          .filter(col => DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field))
          .map(col => col.field);

        // Remove date columns from hidden fields
        const newHiddenFields = new Set(hiddenColumnFields);
        dateColumns.forEach(field => newHiddenFields.delete(field));

        // Update state
        setHiddenColumnFields(newHiddenFields);

        // Get current visibility model
        const currentVisibility =
          apiRef.current?.state.columns.columnVisibilityModel || {};
        const newVisibility = { ...currentVisibility };

        // Show all date columns
        dateColumns.forEach(field => {
          newVisibility[field] = true;
        });

        // Ensure group_name is always visible when groups are enabled
        if (isGroupingEnabled) {
          newVisibility['group_name'] = true;
        }

        // Apply to grid
        if (apiRef.current) {
          apiRef.current.setColumnVisibilityModel(newVisibility);
        }
      }
    },
    [
      filteredData,
      columns,
      hiddenColumnFields,
      setHiddenColumnFields,
      apiRef,
      isGroupingEnabled,
      setHideEmptyDateColumns,
    ]
  );

  // Toggle hide empty date columns (for checkbox)
  const toggleHideEmptyDateColumns = useCallback(() => {
    applyHideEmptyDateColumns(!hideEmptyDateColumns);
  }, [hideEmptyDateColumns, applyHideEmptyDateColumns]);

  // Apply hide empty date columns when data changes
  useEffect(() => {
    if (hideEmptyDateColumns && apiRef.current && !viewManager.loading) {
      // Reapply the hide empty columns filter (using same logic as toggle function)
      const columnsWithValues = new Set<string>();

      BASE_COLUMN_FIELDS.forEach(field => columnsWithValues.add(field));

      filteredData.forEach(row => {
        Object.entries(row).forEach(([key, value]) => {
          if (
            DATE_FORMATS.YYYY_MM_DD_REGEX.test(key) &&
            value !== null &&
            value !== undefined &&
            value !== ''
          ) {
            columnsWithValues.add(key);
          }
        });
      });

      const dateColumns = columns
        .filter(col => DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field))
        .map(col => col.field);

      const emptyDateColumns = dateColumns.filter(
        field => !columnsWithValues.has(field)
      );

      // Create new set keeping non-date hidden columns
      const newHiddenFields = new Set<string>();

      // Keep any non-date columns that were already hidden (but not group_name when groups are enabled)
      hiddenColumnFields.forEach(field => {
        if (
          !DATE_FORMATS.YYYY_MM_DD_REGEX.test(field) &&
          !(isGroupingEnabled && field === 'group_name')
        ) {
          newHiddenFields.add(field);
        }
      });

      // Add empty date columns
      emptyDateColumns.forEach(field => newHiddenFields.add(field));

      // Update state if it changed
      if (
        newHiddenFields.size !== hiddenColumnFields.size ||
        ![...newHiddenFields].every(field => hiddenColumnFields.has(field))
      ) {
        setHiddenColumnFields(newHiddenFields);
      }

      // Update the visibility model
      const currentVisibility =
        apiRef.current.state.columns.columnVisibilityModel || {};
      const newVisibility = { ...currentVisibility };

      // Apply visibility changes
      emptyDateColumns.forEach(field => {
        newVisibility[field] = false;
      });

      // Ensure group_name is always visible when groups are enabled
      if (isGroupingEnabled) {
        newVisibility['group_name'] = true;
      }

      // Apply to grid
      apiRef.current.setColumnVisibilityModel(newVisibility);
    }
  }, [
    hideEmptyDateColumns,
    columns,
    apiRef,
    isGroupingEnabled,
    viewManager.loading,
  ]);

  // Export data handler
  const handleExport = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv();
    }
  }, [apiRef]);

  return {
    filteredData,
    toggleHideEmptyDateColumns,
    applyHideEmptyDateColumns,
    handleExport,
  };
};
